import Helper from '../../../../Common/Helper';
import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { JSX } from 'react';
import { SerendieSymbol } from '@serendie/symbols';
import { IRoleData, UserRoleData } from './DataModels';
import { PagedResponse } from "@iconics/core";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, IGridSorter, OnSectionUpdateHandler, RightButtons, TickSymbol } from '@iconics/components';
import { Guid, TypeHelper } from '@iconics/core';

class EditUserRoles extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            canManageRoles: false,
            showAllRoles: false,
            gridData: { rows: [] },
            gridRefreshToken: ''
        };
    }

    public componentDidMount(): void
    {
        const { localStorageProvider } = appContext();
        this.setState({ canManageRoles: localStorageProvider.hasRight('API.Users.ManageRoles') });
    }

    private async handleRowsRequest(request: IGridRowsRequest<IRole>): Promise<IGridRowsResponse<IRole>> 
    {
        if (this.state.showAllRoles)
        {
            const skip = TypeHelper.toNumber(request.skipToken) ?? 0;
            const availableRolesData = await this.getAvailableRoles(skip, request.take, { sorter: request.sorter });
            const availableRoles = availableRolesData.value.map(i => this.fromRoleDataToRole(i));
            const availableRoleIds = availableRoles.map(i => `'${i.id}'`);

            const assignedRoles = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.roles.get<PagedResponse<UserRoleData[]>>({
                select: UserRoleData,
                tenancy: appContext().tenantId,
                userid: this.props.userId,
                filter: `Id in (${availableRoleIds.join(',')})`,
                top: request.take,
            });

            const assignedIds = assignedRoles.value.map(i => i.Id);
            const availableRolesWithAssigned = availableRoles.map(i => ({ ...i, isAssigned: assignedIds.includes(i.id) }));

            return {
                rows: availableRolesWithAssigned,
                nextSkipToken: (availableRolesData.nextSkip == 0 ? '' : availableRolesData.nextSkip.toString()),
            };
        }
        else
        {
            const assignedRoles = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.roles.get<PagedResponse<UserRoleData[]>>({
                select: UserRoleData,
                tenancy: appContext().tenantId,
                userid: this.props.userId,
                orderBy: DataGrid.toODataOrderBy(request.sorter),
                skipToken: request.skipToken,
                top: request.take,
            });

            const assignedIds = assignedRoles.value.map(i => i.Id);
            const availableRolesData = await this.getAvailableRoles(0, request.take, { filter: i => assignedIds.includes(i.Id) });
            const availableRoles = availableRolesData.value.map(i => this.fromRoleDataToRole(i));
            const availableRolesWithAssigned = availableRoles.map(i => ({ ...i, isAssigned: true }));

            return {
                rows: availableRolesWithAssigned,
                nextSkipToken: assignedRoles.skipToken,
            };
        }
    }

    private availableRoles?: IRoleData[];
    private async getAvailableRoles(skip: number, take: number, options?: { sorter?: IGridSorter, filter?: (role: IRoleData) => boolean }): Promise<{ value: IRoleData[], nextSkip: number }>
    {
        if (!this.availableRoles)
        {
            const availableRoles = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.availableRoles.get<PagedResponse<IRoleData[]>>({
                tenancy: appContext().tenantId,
                userid: this.props.userId,
            });
            this.availableRoles = availableRoles.value;
        }

        const pageOfAvailableRolesPlusOne = DataGrid
            .sort(this.availableRoles, options?.sorter)
            .filter(i => options?.filter?.(i) ?? true)
            .slice(skip, skip + take + 1);

        const pageOfAvailableRoles = pageOfAvailableRolesPlusOne.slice(0, take);

        return {
            value: pageOfAvailableRoles,
            nextSkip: (pageOfAvailableRolesPlusOne.length > take ? skip + take : 0),
        };
    }

    private fromRoleDataToRole(value: IRoleData): IRole
    {
        return {
            id: value.Id,
            name: value.Name,
            identityProviderName: value.IdentityProviderName,
            isActive: TypeHelper.toBoolean(value.Active) ?? false,
            isOwner: TypeHelper.toBoolean(value.IsOwner) ?? false,
            isConfigurable: TypeHelper.toBoolean(value.Configurable) ?? false,
            isAssigned: false,
        };
    }

    private async handleAssignRoles(): Promise<void>
    {
        this.loading(async () =>
        {
            for (const id of this.state.gridData.checkedIds ?? [])
            {
                try
                {
                    if (!this.state.gridData.rows.find(r => r.id === id)?.isConfigurable)
                    {
                        continue;
                    }
                    
                    await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byId.allocateRoles.post({
                        tenancy: appContext().tenantId,
                        id: this.props.userId,
                        body: [{ Id: id }],
                    });

                    this.setRow({ id: id, isAssigned: true });
                }
                catch { }
            }
        });
    }

    private async handleUnassignRoles(): Promise<void>
    {
        this.loading(async () =>
        {
            for (const id of this.state.gridData.checkedIds ?? [])
            {
                try
                {
                    if (!this.state.gridData.rows.find(r => r.id === id)?.isConfigurable)
                    {
                        continue;
                    }

                    await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byId.deallocateRoles.delete({
                        tenancy: appContext().tenantId,
                        id: this.props.userId,
                        body: [id],
                    });

                    this.setRow({ id: id, isAssigned: false });
                }
                catch { }
            }
        });
    }

    private setRow(row: Pick<IRole, "id"> & Partial<IRole>): void
    {
        this.setState(prevState => ({
            gridData:
            {
                ...prevState.gridData,
                rows: prevState.gridData.rows.map(r => r.id === row.id ? { ...r, ...row } : r),
            },
        }));
    }

    private async handleToggleAvailableRoles(showAllRoles: boolean): Promise<void>
    {
        await this.setStateAsync({ showAllRoles: showAllRoles, gridRefreshToken: Guid.new().toString() });
    }

    public render(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_User_RoleListTitle}
                subTitle={this.labels.Platform_User_RoleListSubTitle}
                onSectionUpdate={i => this.props.onSectionUpdate(i)}
            >
                <GridContainer>
                    <GridItem>
                        <GridItem>
                            <ButtonBar>
                                <RightButtons>
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name='close' />}
                                        onClick={() => this.handleUnassignRoles()}
                                        disabled={!this.state.canManageRoles || (this.state.gridData.checkedIds ?? []).length == 0}
                                    >
                                        {this.labels.Platform_User_UnassignRole}
                                    </Button>
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name='check' />}
                                        onClick={() => this.handleAssignRoles()}
                                        disabled={!this.state.canManageRoles || (this.state.gridData.checkedIds ?? []).length == 0}
                                    >
                                        {this.labels.Platform_User_AssignRole}
                                    </Button>
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name={this.state.showAllRoles ? 'eye-hidden' : 'eye'} />}
                                        onClick={() => this.handleToggleAvailableRoles(!this.state.showAllRoles)}
                                    >
                                        {this.state.showAllRoles ? this.labels.Platform_User_HideAvailableRoles : this.labels.Platform_User_ShowAvailableRoles}
                                    </Button>
                                </RightButtons>
                            </ButtonBar>
                        </GridItem>
                        <GridItem>
                            <DataGrid
                                columns={[
                                    {
                                        columnId: Helper.nameOf<IRoleData | UserRoleData>('Name'),
                                        title: this.labels.Role_Name,
                                        getCellValue: i => i.name,
                                        columnWidth: 30,
                                        sortable: true,
                                    },
                                    {
                                        columnId: Helper.nameOf<IRoleData>('IdentityProviderName'),
                                        title: this.labels.Role_IdentityProviderId,
                                        getCellValue: i => i.identityProviderName,
                                        columnWidth: 30,
                                        sortable: false,
                                    },
                                    {
                                        columnId: Helper.nameOf<IRoleData | UserRoleData>('IsOwner'),
                                        title: this.labels.Role_IsOwner,
                                        getCellValue: (i) => <TickSymbol value={i.isOwner} />,
                                        columnWidth: 10,
                                        sortable: true,
                                    },
                                    {
                                        columnId: Helper.nameOf<IRoleData>('Active'),
                                        title: this.labels.Role_IsEnabled,
                                        getCellValue: (i) => <TickSymbol value={i.isActive} />,
                                        columnWidth: 10,
                                        sortable: false,
                                    },
                                    {
                                        columnId: Helper.nameOf<IRole>('isAssigned'),
                                        title: this.labels.Role_IsAssignedToUser,
                                        getCellValue: (i) => <TickSymbol value={i.isAssigned} />,
                                        columnWidth: 10,
                                        sortable: false,
                                    },
                                    {
                                        columnId: Helper.nameOf<IRoleData>('Configurable'),
                                        title: this.labels.Role_IsConfigurable,
                                        getCellValue: (i) => <TickSymbol value={i.isConfigurable} />,
                                        columnWidth: 10,
                                        sortable: false,
                                    },
                                ]}
                                data={this.state.gridData}
                                rowId={i => i.id}
                                onChange={i => this.setState({ gridData: i })}
                                onRowsRequest={i => this.handleRowsRequest(i)}
                                onCellClicked={i => this.props.onRoleClick(i.id)}
                                refreshToken={this.state.gridRefreshToken}
                                showCheckBoxes={true}
                                allowOnlyOneCheckBox={false}
                            />
                        </GridItem>
                    </GridItem>
                </GridContainer>
            </Card>
        )
    }
}

export default EditUserRoles;

export interface IProps
{
    userId: string;
    onRoleClick: (id: string) => void;
    onSectionUpdate: OnSectionUpdateHandler;
}

export interface IState 
{
    canManageRoles: boolean;
    showAllRoles: boolean;
    gridData: IGridData<IRole>;
    gridRefreshToken: string;
}

export interface IRole
{
    id: string;
    name: string;
    identityProviderName: string;
    isActive: boolean;
    isOwner: boolean;
    isConfigurable: boolean;
    isAssigned: boolean;
}
